import React, { useState } from 'react';
import {
  isInquiryProcessAlias,
  isNegotiationProcessAlias,
  OFFER,
} from '../../transactions/transaction';
import { ensureUser, ensureCurrentUser } from '../../util/data';
import { useIntl } from '../../util/reactIntl';
import { NamedLink, Modal } from '../../components';
import InquiryForm from './InquiryForm/InquiryForm';
import css from './SectionAuthorMaybe.module.css';
import modalCss from './ListingPage.module.css';

const CONTACT_USER_LINK = 'inquiryModalContactUserLink';

const StarRating = ({ rating }) => {
  const filled = Math.round(rating || 0);
  return (
    <span className={css.ratingStars}>
      {'★'.repeat(filled)}{'☆'.repeat(5 - filled)}
    </span>
  );
};

const HostAvatar = ({ user }) => {
  const profileImage = user?.profileImage;
  const variants = profileImage?.attributes?.variants || {};
  const imgUrl =
    variants['square-small2x']?.url ||
    variants['square-small']?.url ||
    null;
  const displayName = user?.attributes?.profile?.displayName || '';
  const initials = displayName
    .split(' ')
    .slice(0, 2)
    .map(w => w[0] || '')
    .join('')
    .toUpperCase();

  return (
    <div className={css.avatar}>
      {imgUrl ? (
        <img src={imgUrl} alt={displayName} style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
      ) : (
        <span style={{ fontSize: 22, fontWeight: 600, color: '#6B7280' }}>{initials || '?'}</span>
      )}
    </div>
  );
};

const SectionAuthorMaybe = props => {
  const {
    title,
    listing,
    authorDisplayName,
    onContactUser,
    isInquiryModalOpen,
    onCloseInquiryModal,
    sendInquiryError,
    sendInquiryInProgress,
    onSubmitInquiry,
    currentUser,
    onManageDisableScrolling,
    reviews = [],
  } = props;

  const intl = useIntl();
  const [bioExpanded, setBioExpanded] = useState(false);

  if (!listing.author) return null;

  const author = listing.author;
  const ensuredAuthor = ensureUser(author);
  const ensuredCurrentUser = ensureCurrentUser(currentUser);
  const isCurrentUser =
    ensuredAuthor.id &&
    ensuredCurrentUser.id &&
    ensuredAuthor.id.uuid === ensuredCurrentUser.id.uuid;

  const { displayName, bio } = ensuredAuthor.attributes.profile || {};
  const createdAt = ensuredAuthor.attributes?.createdAt;
  const memberSince = createdAt
    ? intl.formatDate(new Date(createdAt), { month: 'long', year: 'numeric' })
    : null;

  const transactionProcessAlias = listing?.attributes?.publicData?.transactionProcessAlias || '';
  const unitType = listing?.attributes?.publicData?.unitType || '';
  const isInquiryProcess = isInquiryProcessAlias(transactionProcessAlias);
  const isNegotiationProcess = isNegotiationProcessAlias(transactionProcessAlias);
  const showContact = !isCurrentUser && !(isInquiryProcess || (isNegotiationProcess && unitType === OFFER));

  // Compute average rating from reviews
  const avgRating =
    reviews.length > 0
      ? reviews.reduce((sum, r) => sum + (r.attributes?.rating || 0), 0) / reviews.length
      : null;

  const hasBio = !!bio;
  const bioIsLong = hasBio && bio.length > 120;

  return (
    <section id="author" className={css.root}>
      <h2 className={css.sectionTitle}>About Your Host</h2>
      <div className={css.card}>
        <HostAvatar user={author} />
        <div className={css.info}>
          <p className={css.name}>{displayName}</p>
          {memberSince ? (
            <p className={css.memberSince}>Member since {memberSince}</p>
          ) : null}
          {avgRating !== null ? (
            <div className={css.ratingRow}>
              <StarRating rating={avgRating} />
              <span className={css.ratingCount}>
                {avgRating.toFixed(1)} ({reviews.length} {reviews.length === 1 ? 'review' : 'reviews'})
              </span>
            </div>
          ) : null}
          {hasBio ? (
            <>
              <p className={bioExpanded ? css.bioExpanded : css.bio}>{bio}</p>
              {bioIsLong ? (
                <button
                  type="button"
                  className={css.bioToggle}
                  onClick={() => setBioExpanded(e => !e)}
                >
                  {bioExpanded ? 'Show less' : 'Read more'}
                </button>
              ) : null}
            </>
          ) : null}
          <div className={css.actions}>
            {ensuredAuthor.id ? (
              <NamedLink
                className={css.viewProfileBtn}
                name="ProfilePage"
                params={{ id: ensuredAuthor.id.uuid }}
              >
                View profile
              </NamedLink>
            ) : null}
            {showContact ? (
              <button
                id={CONTACT_USER_LINK}
                type="button"
                className={css.contactBtn}
                onClick={() => onContactUser(author)}
              >
                Contact host
              </button>
            ) : null}
          </div>
        </div>
      </div>
      <Modal
        id="ListingPage.inquiry"
        contentClassName={modalCss.inquiryModalContent}
        isOpen={isInquiryModalOpen}
        onClose={onCloseInquiryModal}
        usePortal
        onManageDisableScrolling={onManageDisableScrolling}
        focusElementId={CONTACT_USER_LINK}
      >
        <InquiryForm
          className={modalCss.inquiryForm}
          submitButtonWrapperClassName={modalCss.inquirySubmitButtonWrapper}
          listingTitle={title}
          authorDisplayName={authorDisplayName}
          sendInquiryError={sendInquiryError}
          onSubmit={onSubmitInquiry}
          inProgress={sendInquiryInProgress}
        />
      </Modal>
    </section>
  );
};

export default SectionAuthorMaybe;
